/**
 * useCorrectionPolling — le relevé de la correction pendant qu'on l'attend : 18 relevés, un toutes
 * les 5 s (le dernier à 90 s), puis l'annonce du délai dépassé. Un appelant qui n'attend pas, mais
 * montre la correction quand elle arrive, passe son propre nombre de relevés (`maxPolls`).
 *
 * Déplacé tel quel de `common/hooks/` : mêmes horloges ; seuls des types sont ajoutés.
 */
import { useCallback, useEffect, useRef } from "react"

const MAX_POLLING_COUNT = 18 // 18 polls, one every 5 s: the last one at 90 s

/** L'intervalle entre deux relevés. */
export const CORRECTION_POLL_INTERVAL_MS = 5000

// `onTimeout` (optional) fires once, one tick after the last poll, when
// `shouldPoll` has not turned false — letting callers surface an explicit
// timeout state instead of an indefinite spinner. The last poll comes first:
// an answer that lands at the very end is read, not announced as late.
export const useCorrectionPolling = (
    shouldPoll: boolean,
    onPoll: () => void,
    onTimeout?: () => void,
    maxPolls: number = MAX_POLLING_COUNT
) => {
    const onPollRef = useRef(onPoll)
    onPollRef.current = onPoll
    const onTimeoutRef = useRef(onTimeout)
    onTimeoutRef.current = onTimeout
    const maxPollsRef = useRef(maxPolls)
    maxPollsRef.current = maxPolls

    const pollingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
    const pollingCountRef = useRef(0)

    const stopPolling = useCallback(() => {
        if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current)
            pollingIntervalRef.current = null
        }
        pollingCountRef.current = 0
    }, [])

    const startPolling = useCallback(() => {
        if (pollingIntervalRef.current) return
        pollingIntervalRef.current = setInterval(() => {
            pollingCountRef.current += 1
            if (pollingCountRef.current > maxPollsRef.current) {
                stopPolling()
                onTimeoutRef.current?.()
                return
            }
            onPollRef.current()
        }, CORRECTION_POLL_INTERVAL_MS)
    }, [stopPolling])

    useEffect(() => {
        if (shouldPoll) {
            startPolling()
        } else {
            stopPolling()
        }
        return () => stopPolling()
    }, [shouldPoll, startPolling, stopPolling])
}
